/**
 * pagina detalhe de um item (dentro de uma pesquisa).
 *  - header do item (descricao, CATMAT, unidade, qtd)
 *  - secao cotacoes: form manual, sync PNCP, tabela com excluir
 *  - secao cesta: form (metodo + justificativa) quando nao formada,
 *    ou visualizacao quando formada
 *
 * o item nao tem endpoint dedicado — usamos useGetPesquisa e filtramos
 * o item dentro da resposta. evita um endpoint novo no api.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  CestaFormarSchema,
  type CestaFormar,
  CotacaoCreateSchema,
  type CotacaoCreate,
  type ItemPesquisa,
  type MetodoEstatisticoT,
} from '@licita-mind/shared-types';
import { toast } from 'sonner';
import { AlertaRiscoCard } from '../components/AnaliseRiscoCard';
import { CotacaoStatusBadge, FonteBadge } from '../components/FonteBadge';
import { Logo } from '../components/Logo';
import { MoneyDisplay, formatarCentavos } from '../components/MoneyDisplay';
import { PromptModal } from '../components/PromptModal';
import { useAnaliseRisco } from '../hooks/useAnaliseRisco';
import { useGetPesquisa } from '../hooks/usePesquisa';
import {
  useCreateCotacao,
  useExcluirCotacao,
  useFormarCesta,
  useGetCesta,
  useListCotacoes,
  useSyncPncp,
} from '../hooks/useCotacoes';
import { useSimilares } from '../hooks/useSimilares';
import { ApiError } from '../lib/api';

const hojeIso = (): string => new Date().toISOString().slice(0, 10);
const fmtDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });

const cotacaoVazia = (): CotacaoCreate => ({
  fonte: 'COTACAO_DIRETA_FORNECEDOR',
  fonte_url: null,
  fornecedor_cnpj: null,
  fornecedor_nome: null,
  preco_unitario_centavos: 0,
  data_referencia: hojeIso(),
  referencia_externa: {},
  metadata_coleta: {},
});

export const ItemDetalhePage = () => {
  const { id, itemId } = useParams<{ id: string; itemId: string }>();
  const { data: pesquisa } = useGetPesquisa(id);
  const { data: cotacoes } = useListCotacoes(itemId);
  const { data: cesta } = useGetCesta(itemId);

  const createCot = useCreateCotacao(itemId ?? '');
  const excluirCot = useExcluirCotacao(itemId ?? '');
  const syncPncp = useSyncPncp(itemId ?? '');
  const formarCesta = useFormarCesta(itemId ?? '');
  const { data: similares } = useSimilares(itemId, 5);
  const { data: risco } = useAnaliseRisco(itemId);

  const [showCotForm, setShowCotForm] = useState(false);
  const [novaCot, setNovaCot] = useState<CotacaoCreate>(cotacaoVazia());
  const [cotErrors, setCotErrors] = useState<Record<string, string>>({});

  const [showCestaForm, setShowCestaForm] = useState(false);
  const [cestaForm, setCestaForm] = useState<CestaFormar>({
    metodo: 'MEDIA_SANEADA_TUKEY',
    justificativa_metodo: '',
  });
  const [cestaErrors, setCestaErrors] = useState<Record<string, string>>({});
  const [excluirAlvo, setExcluirAlvo] = useState<string | null>(null);

  const item: ItemPesquisa | undefined = pesquisa?.itens.find((i) => i.id === itemId);

  if (!pesquisa || !item) {
    return (
      <Layout pesquisaId={id}>
        <p className="text-center text-navy-500 text-sm py-12">carregando...</p>
      </Layout>
    );
  }

  const cotacoesValidas = cotacoes?.data.filter((c) => c.status === 'VALIDA') ?? [];
  const podeFormarCesta = cotacoesValidas.length >= 3;
  const itemTemCodigo = !!(item.catmat || item.catser);

  const submitCotacao = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setCotErrors({});
    const parsed = CotacaoCreateSchema.safeParse(novaCot);
    if (!parsed.success) {
      const flat: Record<string, string> = {};
      for (const issue of parsed.error.issues) flat[issue.path.join('.')] = issue.message;
      setCotErrors(flat);
      return;
    }
    createCot.mutate(parsed.data, {
      onSuccess: () => {
        toast.success('cotação registrada');
        setShowCotForm(false);
        setNovaCot(cotacaoVazia());
      },
      onError: (err) =>
        toast.error('falha ao registrar', {
          description: err instanceof Error ? err.message : String(err),
        }),
    });
  };

  const submitCesta = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setCestaErrors({});
    const parsed = CestaFormarSchema.safeParse(cestaForm);
    if (!parsed.success) {
      const flat: Record<string, string> = {};
      for (const issue of parsed.error.issues) flat[issue.path.join('.')] = issue.message;
      setCestaErrors(flat);
      return;
    }
    formarCesta.mutate(parsed.data, {
      onSuccess: (c) => {
        toast.success('cesta formada', {
          description: `preço de referência: R$ ${(Number(c.preco_referencia_centavos) / 100).toFixed(2).replace('.', ',')} · ${c.estatisticas.cotacoesValidas} válidas`,
        });
        setShowCestaForm(false);
      },
      onError: (err) =>
        toast.error('falha ao formar cesta', {
          description: err instanceof Error ? err.message : String(err),
        }),
    });
  };

  const onExcluir = (cotacaoId: string): void => setExcluirAlvo(cotacaoId);
  const confirmarExclusao = (motivo: string): void => {
    if (!excluirAlvo) return;
    excluirCot.mutate(
      { cotacaoId: excluirAlvo, motivo },
      {
        onSuccess: () => {
          toast.success('cotação excluída', { description: motivo.slice(0, 80) });
          setExcluirAlvo(null);
        },
        onError: (err) => {
          toast.error('falha ao excluir', {
            description: err instanceof Error ? err.message : String(err),
          });
        },
      },
    );
  };

  const createErr = createCot.error instanceof ApiError ? createCot.error : null;
  const syncErr = syncPncp.error instanceof ApiError ? syncPncp.error : null;
  const formarErr = formarCesta.error instanceof ApiError ? formarCesta.error : null;
  const excluirErr = excluirCot.error instanceof ApiError ? excluirCot.error : null;

  return (
    <Layout pesquisaId={id}>
      <div className="mb-6">
        <Link to={`/pesquisas/${id}`} className="text-sm text-navy-500 hover:text-navy-700">
          ← {pesquisa.objeto}
        </Link>
      </div>

      <div className="card mb-6">
        <h2 className="text-xl font-display font-semibold mb-2">{item.descricao}</h2>
        {item.especificacao_tecnica && (
          <p className="text-sm text-navy-600 mb-3">{item.especificacao_tecnica}</p>
        )}
        <div className="flex flex-wrap gap-4 text-sm text-navy-500">
          <span>
            unidade: <strong className="text-navy-700">{item.unidade}</strong>
          </span>
          <span>
            quantidade: <strong className="text-navy-700 tabular-nums">{item.quantidade}</strong>
          </span>
          {item.catmat && (
            <span>
              CATMAT: <code className="font-mono text-navy-700">{item.catmat}</code>
            </span>
          )}
          {item.catser && (
            <span>
              CATSER: <code className="font-mono text-navy-700">{item.catser}</code>
            </span>
          )}
        </div>
      </div>

      <section className="card mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display font-semibold text-lg">cotações ({cotacoes?.data.length ?? 0})</h3>
            <p className="text-xs text-navy-500 mt-0.5">
              {cotacoesValidas.length} válidas — mínimo 3 pra formar cesta (TCU 1.445/2015)
            </p>
          </div>
          <div className="flex gap-2">
            {itemTemCodigo && (
              <button
                type="button"
                onClick={() =>
                  syncPncp.mutate(undefined, {
                    onSuccess: (r) =>
                      toast.success(`sync PNCP: ${r.inseridas} novas`, {
                        description: `${r.duplicadas} duplicadas, ${r.ignoradas_sem_preco} sem preço`,
                      }),
                    onError: (err) =>
                      toast.error('sync PNCP falhou', {
                        description: err instanceof Error ? err.message : String(err),
                      }),
                  })
                }
                disabled={syncPncp.isPending}
                className="btn-secondary text-sm"
                title="busca cotações no PNCP por CATMAT/CATSER"
              >
                {syncPncp.isPending ? 'sincronizando...' : '↻ sync PNCP'}
              </button>
            )}
            {!showCotForm && (
              <button
                type="button"
                onClick={() => setShowCotForm(true)}
                className="btn-secondary text-sm"
              >
                + registrar cotação
              </button>
            )}
          </div>
        </div>

        {syncPncp.data && (
          <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm text-emerald-800 mb-4">
            sync PNCP: <strong>{syncPncp.data.inseridas}</strong> inseridas,{' '}
            <strong>{syncPncp.data.duplicadas}</strong> duplicadas,{' '}
            <strong>{syncPncp.data.ignoradas_sem_preco}</strong> sem preço (janela{' '}
            {syncPncp.data.janela_inicio} → {syncPncp.data.janela_fim})
          </div>
        )}
        {syncErr && (
          <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700 mb-4">
            sync PNCP: {syncErr.message}
          </div>
        )}
        {excluirErr && (
          <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700 mb-4">
            exclusão: {excluirErr.message}
          </div>
        )}

        {showCotForm && (
          <form
            onSubmit={submitCotacao}
            className="border border-navy-200 rounded-md p-4 mb-4 bg-navy-50/30 space-y-3"
          >
            {createErr && (
              <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">
                {createErr.code}: {createErr.message}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">fonte</label>
                <select
                  className="input"
                  value={novaCot.fonte}
                  onChange={(e) =>
                    setNovaCot((v) => ({
                      ...v,
                      fonte: e.target.value as CotacaoCreate['fonte'],
                    }))
                  }
                >
                  <option value="COTACAO_DIRETA_FORNECEDOR">cotação direta fornecedor</option>
                  <option value="SITIO_ESPECIALIZADO">sítio especializado</option>
                  <option value="CONTRATACAO_SIMILAR_MUNICIPIO">contratação similar</option>
                  <option value="PNCP">PNCP (manual)</option>
                  <option value="PAINEL_PRECOS">Painel de Preços</option>
                </select>
              </div>
              <div>
                <label className="label">preço unitário (R$)</label>
                <input
                  type="number"
                  className="input"
                  min="0"
                  step="0.01"
                  value={novaCot.preco_unitario_centavos / 100}
                  onChange={(e) =>
                    setNovaCot((v) => ({
                      ...v,
                      preco_unitario_centavos: Math.round(Number(e.target.value) * 100),
                    }))
                  }
                  required
                />
                {cotErrors.preco_unitario_centavos && (
                  <p className="error">{cotErrors.preco_unitario_centavos}</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">data de referência</label>
                <input
                  type="date"
                  className="input"
                  value={novaCot.data_referencia}
                  onChange={(e) => setNovaCot((v) => ({ ...v, data_referencia: e.target.value }))}
                  max={hojeIso()}
                  required
                />
              </div>
              <div>
                <label className="label">fornecedor (opcional)</label>
                <input
                  className="input"
                  placeholder="razão social"
                  value={novaCot.fornecedor_nome ?? ''}
                  onChange={(e) => setNovaCot((v) => ({ ...v, fornecedor_nome: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <label className="label">URL da fonte (opcional)</label>
              <input
                className="input"
                type="url"
                placeholder="https://..."
                value={novaCot.fonte_url ?? ''}
                onChange={(e) => setNovaCot((v) => ({ ...v, fonte_url: e.target.value }))}
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowCotForm(false)}
                className="btn-ghost text-sm"
              >
                cancelar
              </button>
              <button type="submit" className="btn-primary text-sm" disabled={createCot.isPending}>
                {createCot.isPending ? 'registrando...' : 'registrar'}
              </button>
            </div>
          </form>
        )}

        {(!cotacoes || cotacoes.data.length === 0) && !showCotForm && (
          <p className="text-sm text-navy-500 py-6 text-center">
            ainda sem cotações. registre manualmente ou sincronize com PNCP.
          </p>
        )}

        {cotacoes && cotacoes.data.length > 0 && (
          <table className="w-full text-sm">
            <thead className="text-xs text-navy-500 uppercase border-b border-navy-100">
              <tr>
                <th className="text-left py-2 font-medium">fonte / fornecedor</th>
                <th className="text-right py-2 font-medium">preço</th>
                <th className="text-center py-2 font-medium">data</th>
                <th className="text-center py-2 font-medium">status</th>
                <th className="text-right py-2 font-medium">ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-navy-100">
              {cotacoes.data.map((c) => (
                <tr key={c.id}>
                  <td className="py-3">
                    <FonteBadge fonte={c.fonte} />
                    {c.fornecedor_nome && (
                      <div className="text-xs text-navy-500 mt-1">{c.fornecedor_nome}</div>
                    )}
                  </td>
                  <td className="py-3 text-right tabular-nums font-medium">
                    <MoneyDisplay centavos={c.preco_unitario_centavos} />
                  </td>
                  <td className="py-3 text-center text-navy-500 text-xs">
                    {fmtDate(c.data_referencia)}
                  </td>
                  <td className="py-3 text-center">
                    <CotacaoStatusBadge status={c.status} />
                    {c.motivo_exclusao && (
                      <div
                        className="text-xs text-navy-400 mt-1 max-w-xs mx-auto"
                        title={c.motivo_exclusao}
                      >
                        {c.motivo_exclusao.length > 50
                          ? `${c.motivo_exclusao.slice(0, 50)}...`
                          : c.motivo_exclusao}
                      </div>
                    )}
                  </td>
                  <td className="py-3 text-right">
                    {c.status === 'VALIDA' && (
                      <button
                        type="button"
                        onClick={() => onExcluir(c.id)}
                        className="text-xs text-red-600 hover:text-red-800"
                        disabled={excluirCot.isPending}
                      >
                        excluir
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display font-semibold text-lg">cesta de preços</h3>
            <p className="text-xs text-navy-500 mt-0.5">
              subset saneado com método estatístico justificado (TCU 1.875/2021)
            </p>
          </div>
          {cesta && !showCestaForm && (
            <button
              type="button"
              onClick={() => setShowCestaForm(true)}
              className="btn-secondary text-sm"
            >
              re-formar cesta
            </button>
          )}
        </div>

        {formarErr && (
          <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700 mb-4">
            {formarErr.code}: {formarErr.message}
          </div>
        )}

        {!cesta && !showCestaForm && (
          <div className="text-center py-6">
            <p className="text-sm text-navy-500 mb-4">
              {podeFormarCesta
                ? `${cotacoesValidas.length} cotações válidas — pronto pra formar cesta.`
                : `${cotacoesValidas.length}/3 cotações válidas — precisa de pelo menos 3.`}
            </p>
            <button
              type="button"
              onClick={() => setShowCestaForm(true)}
              className="btn-primary text-sm"
              disabled={!podeFormarCesta}
            >
              formar cesta
            </button>
          </div>
        )}

        {showCestaForm && (
          <form
            onSubmit={submitCesta}
            className="border border-navy-200 rounded-md p-4 mb-4 bg-navy-50/30 space-y-3"
          >
            <div>
              <label className="label">método estatístico</label>
              <select
                className="input"
                value={cestaForm.metodo}
                onChange={(e) =>
                  setCestaForm((v) => ({ ...v, metodo: e.target.value as MetodoEstatisticoT }))
                }
              >
                <option value="MEDIA_SANEADA_TUKEY">média saneada (Tukey) — recomendado</option>
                <option value="MEDIANA">mediana — robusta a outliers</option>
                <option value="MEDIA_SANEADA_ZSCORE">média saneada (Z-score)</option>
              </select>
            </div>
            <div>
              <label className="label">
                justificativa do método (obrigatória — TCU 1.875/2021)
              </label>
              <textarea
                className="input"
                rows={3}
                placeholder="por que esse método? quais cotações esperamos excluir e por quê?"
                value={cestaForm.justificativa_metodo}
                onChange={(e) =>
                  setCestaForm((v) => ({ ...v, justificativa_metodo: e.target.value }))
                }
                required
                minLength={1}
                maxLength={2000}
              />
              {cestaErrors.justificativa_metodo && (
                <p className="error">{cestaErrors.justificativa_metodo}</p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowCestaForm(false)}
                className="btn-ghost text-sm"
              >
                cancelar
              </button>
              <button
                type="submit"
                className="btn-primary text-sm"
                disabled={formarCesta.isPending || !podeFormarCesta}
              >
                {formarCesta.isPending ? 'formando...' : 'formar cesta'}
              </button>
            </div>
          </form>
        )}

        {cesta && !showCestaForm && (
          <div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
              <div className="text-xs text-emerald-700 uppercase font-medium mb-1">
                preço de referência
              </div>
              <div className="text-3xl font-display font-bold text-emerald-900 tabular-nums">
                <MoneyDisplay centavos={cesta.preco_referencia_centavos} />
              </div>
              <div className="text-xs text-emerald-800 mt-2">
                método: <strong>{cesta.metodo.replace(/_/g, ' ').toLowerCase()}</strong> ·{' '}
                {cesta.estatisticas.cotacoesValidas} incluídas /{' '}
                {cesta.estatisticas.cotacoesExcluidas} excluídas · formada{' '}
                {fmtDate(cesta.formada_em)}
              </div>
            </div>

            <details className="text-sm">
              <summary className="cursor-pointer text-navy-600 hover:text-navy-800 font-medium mb-2">
                estatísticas detalhadas
              </summary>
              <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-xs">
                {[
                  ['mínimo', cesta.estatisticas.minimo],
                  ['máximo', cesta.estatisticas.maximo],
                  ['mediana', cesta.estatisticas.mediana],
                  ['média', cesta.estatisticas.media],
                  ['desvio padrão', cesta.estatisticas.desvioPadrao],
                  ['Q1', cesta.estatisticas.q1],
                  ['Q3', cesta.estatisticas.q3],
                  ['IQR', cesta.estatisticas.iqr],
                ].map(([label, value]) => (
                  <div key={label} className="bg-navy-50 rounded p-2">
                    <dt className="text-navy-500 uppercase text-[10px]">{label}</dt>
                    <dd className="font-mono text-navy-900 mt-0.5">
                      {formatarCentavos(value as number)}
                    </dd>
                  </div>
                ))}
              </dl>
            </details>

            <div className="mt-4">
              <p className="text-xs text-navy-500 uppercase font-medium mb-2">
                justificativa metodológica
              </p>
              <p className="text-sm text-navy-700 italic bg-navy-50/50 p-3 rounded border border-navy-100">
                {cesta.justificativa_metodo}
              </p>
            </div>
          </div>
        )}
      </section>

      {risco && risco.alertas.length > 0 && (
        <section className="card mt-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="font-display font-semibold text-lg">análise de risco TCU</h3>
              <p className="text-xs text-navy-500 mt-0.5">
                {risco.resumo.total} alertas: {risco.resumo.alta} alto,{' '}
                {risco.resumo.media} médio, {risco.resumo.baixa} baixo
              </p>
            </div>
          </div>
          <div className="space-y-3">
            {risco.alertas.map((a) => (
              <AlertaRiscoCard key={a.regra_id} alerta={a} />
            ))}
          </div>
        </section>
      )}

      {risco && risco.alertas.length === 0 && (
        <section className="card mt-6 bg-emerald-50/50 border-emerald-200">
          <div className="flex items-center gap-3">
            <span className="text-2xl">✓</span>
            <div>
              <h3 className="font-display font-semibold text-emerald-900">
                análise de risco TCU
              </h3>
              <p className="text-sm text-emerald-800">
                nenhum alerta detectado — cesta dentro dos parâmetros recomendados.
              </p>
            </div>
          </div>
        </section>
      )}

      {similares && similares.data.length > 0 && (
        <section className="card mt-6">
          <h3 className="font-display font-semibold text-lg mb-1">
            itens similares em pesquisas anteriores
          </h3>
          <p className="text-xs text-navy-500 mb-4">
            busca semântica via embeddings (sentence-transformers
            multilingual-mpnet) entre itens do seu município.
          </p>
          <ul className="divide-y divide-navy-100">
            {similares.data.map((s) => (
              <li key={s.id} className="py-3">
                <Link
                  to={`/pesquisas/${s.pesquisa_id}/itens/${s.id}`}
                  className="block hover:bg-navy-50/50 -mx-3 px-3 py-1 rounded transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-navy-900">{s.descricao}</div>
                      <div className="text-xs text-navy-500 mt-0.5">
                        pesquisa: {s.pesquisa_objeto}
                      </div>
                      <div className="text-xs text-navy-400 mt-0.5">
                        similaridade {(1 - s.distancia / 2).toFixed(2)} ·{' '}
                        {s.qtd_cotacoes_validas} cotações válidas
                        {(s.catmat || s.catser) && (
                          <>
                            {' · '}
                            <code className="font-mono">{s.catmat ?? s.catser}</code>
                          </>
                        )}
                      </div>
                    </div>
                    {s.preco_referencia_centavos !== null && (
                      <div className="text-right">
                        <div className="text-xs text-navy-500">preço ref.</div>
                        <div className="font-medium tabular-nums">
                          <MoneyDisplay centavos={s.preco_referencia_centavos} />
                        </div>
                      </div>
                    )}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <PromptModal
        open={excluirAlvo !== null}
        titulo="excluir cotação"
        descricao="o motivo será gravado em audit e visivel no documento final. obrigatório, 1-500 caracteres."
        placeholder="ex: fornecedor sob investigação TCU, preço fora da realidade do mercado..."
        labelOk={excluirCot.isPending ? 'excluindo...' : 'excluir'}
        maxLength={500}
        minLength={3}
        onClose={() => setExcluirAlvo(null)}
        onSubmit={confirmarExclusao}
      />
    </Layout>
  );
};

const Layout = ({
  children,
  pesquisaId,
}: {
  children: React.ReactNode;
  pesquisaId: string | undefined;
}) => (
  <div className="min-h-screen flex flex-col">
    <header className="bg-white border-b border-navy-100">
      <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
        <Link to="/" aria-label="ir pra lista">
          <Logo />
        </Link>
        {pesquisaId && (
          <Link to={`/pesquisas/${pesquisaId}`} className="btn-ghost text-sm">
            ← pesquisa
          </Link>
        )}
      </div>
    </header>
    <main className="flex-1 max-w-4xl mx-auto w-full px-4 py-8">{children}</main>
    <footer className="bg-white border-t border-navy-100 py-4">
      <p className="text-center text-xs text-navy-400">by lions intelligence</p>
    </footer>
  </div>
);
